import React from 'react';
import { createClient } from 'redis';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';
import classNames from 'classnames';
import StickyBox from 'react-sticky-box';

import { formatUrl } from '../../lib/formatUrl';
import { getPosts, getTag, getTags } from '../../lib/graphqlQuery';
import { log_info, log_error } from '../../utils/logger';
import { useMediaQuery } from '../../utils/hooks/useMediaQuery';
import BREAK_POINTS from '../../utils/breakpoint';
import { camelCase, titleCleansing } from '../../lib/stringManipulate';

import { SiteHead } from '../../components/document/Head/Head';
import FooterBanner from '../../components/Banners/FooterBanner/FooterBanner';
import ArticlePosts from '../../components/ArticlePosts/ArticlePosts';
import SidebarLeadForm from '../../components/LeadForm/SidebarLeadForm/SidebarLeadForm';

import styles from '../../styles/pages/TagPage.module.scss';
import { REDIS_TTL, REDIS_TTL_ERROR } from '../../lib/constant';

const { REDIS_HOST, REDIS_URL } = getConfig().serverRuntimeConfig;

const TagPage = (props) => {
  const router = useRouter();
  const isDesktop = useMediaQuery(BREAK_POINTS.desktop);

  const { posts, tag } = props;

  const currentURL = formatUrl(router.basePath, router.asPath);
  const month = new Date().toLocaleString('id', { month: 'long' });
  const year = new Date().getFullYear();
  const title = tag.seo && tag.seo.title ? tag.seo.title : tag.name;

  return (
    <>
      <SiteHead tag={tag} title={title} url={currentURL} />
      <div className={styles['tag-page']}>
        <div className="row">
          <div className={classNames(styles['row__content'], 'col-lg-8 col-md-12')}>
            <h1 className={styles['tag-page__title']}>{tag.name || tag?.seo?.title}</h1>
            <div className={styles['tag-page__description']}>
              {`Update terbaru ${tag.name ?? ""} bulan "${month}" "${year}", baca artikel selengkapnya hanya di "Lifepal" Dapatkan tip, cara dan informasi tentang asuransi mobil, asuransi kesehatan, asuransi jiwa dan anggaran rumah sakit di Lifepal!.`}
            </div>
            <div className={styles['tag-page__list']}>
              <ArticlePosts posts={posts} tag={tag.slug} inArticleBanners={tag.inArticleBanners} />
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            {isDesktop && (
              <StickyBox offsetTop={100} className={styles['tag-page__sideBar']}>
                <div>
                  <SidebarLeadForm category="media" />
                </div>
              </StickyBox>
            )}
          </div>
        </div>
      </div>
      {!isDesktop && <FooterBanner />}
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  let data;

  if (typeof params.slug === 'string' && params.slug.indexOf(' ') >= 0) {
    return {
      redirect: {
        destination: '/',
        statusCode: 301,
      },
    };
  }

  log_info('getTag', 'param', params.slug);
  if (REDIS_HOST) {
    const redisKey = `tag:${params.slug}`;
    const redis = createClient({ url: REDIS_URL });
    redis.on('error', (err) => log_error('Redis Createclient error on getTag', 'error', err));

    await redis.connect();
    const value = await redis.get(redisKey);

    if (!value) {
      const { data: dataTag } = await getTag(params.slug);
      data = dataTag;
      let redisTTL = REDIS_TTL;
      if (data.status && data.status !== 200) {
        redisTTL = REDIS_TTL_ERROR;
        log_error('getTag', 'error', data);
      }
      await redis.setEx(redisKey, redisTTL, JSON.stringify(dataTag));
      log_info('getTag', 'response', `Key ${redisKey} stored in redis`);
    } else {
      data = JSON.parse(value);
      log_info('getTag', 'response', `Key ${redisKey} fetched from redis`);
    }

    await redis.quit();
  } else {
    const { data: dataTag } = await getTag(params.slug);
    data = dataTag;
    log_error('getTag: Redis option', 'error', 'Redis is not available');
  }
  log_info('getTag', 'response', data);

  log_info('getPosts', 'param', { tag: params.slug, first: 20 });
  const dataPost = await getPosts({ tag: params.slug, first: 20 });
  log_info('getPosts', 'response', dataPost);

  return {
    notFound: !data.tag,
    props: {
      posts: dataPost.data.posts,
      tag: data.tag,
    },
    revalidate: 2 * 60, // cache API response for 2 minutes
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await getTags();
  log_info('getTags', 'response', data);

  return {
    paths:
      data.tags && data.tags.edges ? data.tags.edges.map(({ node }) => `/tag/${node.slug}`) : [],
    fallback: 'blocking',
  };
};

export default TagPage;
